import React from 'react'
import { cn } from '@/lib/utils'

export interface BadgeProps {
  /** Badge content */
  children: React.ReactNode
  /** Color tone */
  tone?: 'info' | 'success' | 'warning' | 'critical' | 'attention' | 'new' | 'purple'
  /** Size variant */
  size?: 'small' | 'medium'
  /** Additional class name */
  className?: string
  /** Native tooltip on hover */
  title?: string
  /** Data ID for testing */
  'data-id'?: string
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  tone = 'info',
  size = 'medium',
  className,
  title,
  'data-id': dataId,
}) => {
  const toneClasses = {
    info: 'bg-[#E5E5E5] text-[#1A1A1A] dark:bg-muted dark:text-foreground',
    success: 'bg-[#AEE9D1] text-[#006D4F] dark:bg-green-500/15 dark:text-green-400',
    warning: 'bg-[#FFEA8A] text-[#7D5C00] dark:bg-yellow-500/15 dark:text-yellow-400',
    critical: 'bg-[#FED3D1] text-[#B10C00] dark:bg-red-500/15 dark:text-red-400',
    attention: 'bg-[#FFD79D] text-[#9D4D00] dark:bg-orange-500/15 dark:text-orange-400',
    new: 'bg-[#005BD3] text-white dark:bg-blue-500/20 dark:text-blue-400',
    purple: 'bg-[#EDDBFF] text-[#5C1E9E] dark:bg-purple-500/15 dark:text-purple-400',
  }

  const sizeClasses = {
    small: 'text-[11px] px-1.5 py-0.5',
    medium: 'text-[12px] px-2 py-0.5',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-md whitespace-nowrap',
        sizeClasses[size],
        toneClasses[tone],
        className
      )}
      title={title}
      data-id={dataId}
    >
      {children}
    </span>
  )
}
